import { Fragment, useEffect, useState } from 'react';
import { crdbRequestsAPI } from '../../services/api';

function statusBadge(code) {
  const n = Number(code);
  const cls = n === 200
    ? 'bg-emerald-100 text-emerald-700'
    : n === 203 || n === 206 || n === 207
      ? 'bg-amber-100 text-amber-700'
      : 'bg-red-100 text-red-700';
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${cls}`}>{code}</span>;
}

function actionBadge(action) {
  const cls = action === 'verify' ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700';
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase ${cls}`}>{action}</span>;
}

export default function CrdbRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1, has_next: false, has_previous: false });
  const [expanded, setExpanded] = useState(null);

  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [statusCode, setStatusCode] = useState('');

  function load() {
    setLoading(true);
    crdbRequestsAPI.list({ page, limit: 25, search, action, status_code: statusCode })
      .then((res) => {
        setRequests(res.requests || []);
        setPagination({
          page: res.page || 1,
          total: res.total || 0,
          total_pages: res.total_pages || 1,
          has_next: !!res.has_next,
          has_previous: !!res.has_previous,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(load, [page, action, statusCode]);

  let searchTimer;
  function handleSearchChange(e) {
    const v = e.target.value;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => { setSearch(v); setPage(1); }, 350);
  }

  function prettyJson(str) {
    try { return JSON.stringify(JSON.parse(str), null, 2); }
    catch { return str; }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-800">CRDB Requests</h1>
        <p className="text-sm text-slate-400 mt-0.5">
          {pagination.total.toLocaleString()} call(s) received on /crdb/verify and /crdb/pay — recorded regardless of the app's log level.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <input type="text" placeholder="Search reference or IP…" onChange={handleSearchChange}
            className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          <select value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }}
            className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
            <option value="">All Actions</option>
            <option value="verify">Verify</option>
            <option value="pay">Pay</option>
          </select>
          <select value={statusCode} onChange={(e) => { setStatusCode(e.target.value); setPage(1); }}
            className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
            <option value="">All Status Codes</option>
            <option value="200">200 — Success</option>
            <option value="201">201 — Invalid token</option>
            <option value="202">202 — Invalid checksum</option>
            <option value="203">203 — Already paid</option>
            <option value="204">204 — Invalid reference</option>
            <option value="205">205 — Expired</option>
            <option value="206">206 — Duplicate entry</option>
            <option value="207">207 — Transaction already paid</option>
          </select>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm">Loading…</div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-1">
            <p className="text-slate-500 font-medium text-sm">No CRDB requests recorded yet</p>
            <p className="text-xs text-slate-400">This fills in the moment CRDB calls /crdb/verify or /crdb/pay</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Date &amp; Time</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Reference</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">IP Address</th>
                  <th className="px-4 py-3 w-16" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {requests.map((r) => (
                  <Fragment key={r.id}>
                    <tr className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap font-mono">{r.created_at}</td>
                      <td className="px-4 py-3">{actionBadge(r.action)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.payment_reference || '—'}</td>
                      <td className="px-4 py-3">
                        {statusBadge(r.status_code)}
                        <span className="ml-2 text-xs text-slate-400">{r.status_desc}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400 font-mono">{r.ip_address || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                          className={`h-4 w-4 text-slate-400 transition-transform ${expanded === r.id ? 'rotate-180' : ''}`}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
                        </svg>
                      </td>
                    </tr>
                    {expanded === r.id && (
                      <tr>
                        <td colSpan={6} className="bg-slate-50 px-4 py-4">
                          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Request</p>
                              <pre className="rounded-lg bg-white border border-slate-200 p-3 text-[11px] text-slate-700 whitespace-pre-wrap break-all font-mono max-h-64 overflow-y-auto">
                                {prettyJson(r.request_body)}
                              </pre>
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Our Response</p>
                              <pre className="rounded-lg bg-white border border-slate-200 p-3 text-[11px] text-slate-700 whitespace-pre-wrap break-all font-mono max-h-64 overflow-y-auto">
                                {prettyJson(r.response_body)}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && requests.length > 0 && (
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
            <p className="text-xs text-slate-400">
              Page {pagination.page} of {pagination.total_pages} · {pagination.total.toLocaleString()} total
            </p>
            <div className="flex gap-1.5">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={!pagination.has_previous}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ← Prev
              </button>
              <button onClick={() => setPage((p) => p + 1)} disabled={!pagination.has_next}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
